/**
 * §M1 · "My profiles" model — DERIVED, never hand-written.
 *
 * The screen answers three questions for each academy the user has a
 * membership in: **what I am**, **what it lets me do**, and —the one that
 * actually matters— **why I CAN'T do the rest**. That last one tells apart
 * two things the user experiences the same way but that are fixed in
 * opposite ways:
 *
 *  · `role`    → you lack rank. Fixed by asking an admin for it.
 *  · `feature` → your academy turned that capability off. No promotion
 *                brings it back; the feature has to be turned on (and the
 *                data is still there).
 *
 * The three sources are the SAME ones that govern the real system:
 *  1. `nav-model` — which screens exist and who sees them (`visible` + `feature`).
 *  2. RBAC contract (`orgRoleGrants` over `ORG_CAPABILITIES`) — what the role grants.
 *  3. Feature manifest — readable name, implications and link to the guide.
 *
 * This module is PURE (no hooks or DOM) so the whole matrix can be tested
 * in vitest: if the explanation derived from enforcement, the test would scream.
 */
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  type FeatureGraphNode,
  ORG_CAPABILITIES,
  type OrgCapability,
  type OrgRole,
  orgRoleGrants,
} from "@space/shared";

import { NAV_SECTIONS, type NavContext, type NavItem } from "@/features/navigation/nav-model";
import { helpTargetForArea } from "@/features/help/registry";
import type { HelpTarget } from "@/features/help/types";

/** Why a screen isn't available. Never both at once. */
export type DenialReason = "role" | "feature";

/** A product screen, viewed from a specific membership. */
export interface ScreenAccess {
  /** `nav-<slug>` — same stable id as the sidebar. */
  testid: string;
  to: string;
  label: MessageDescriptor;
  sectionId: string;
  allowed: boolean;
  /** Only when `allowed === false`. */
  reason?: DenialReason;
  /** Feature gating the screen, if any (whether or not it's blocked). */
  feature?: string;
  /**
   * Graph key that actually blocks it: it can be a REQUIREMENT of `feature`,
   * not `feature` itself. Telling the user "X is missing" when the culprit
   * is its dependency Y sends them to fight the wrong switch.
   */
  blockedBy?: string;
  /** Guide + section documenting this screen (if the area is mapped). */
  help?: HelpTarget;
}

/** An organization-plane capability, viewed from a role. */
export interface CapabilityAccess {
  capability: OrgCapability;
  granted: boolean;
  label: MessageDescriptor;
  /** What it means in practice (explanatory text, §M4). */
  description: MessageDescriptor;
}

/** RBAC contract labels. Exhaustive by type: missing one ⇒ doesn't compile. */
const CAPABILITY_COPY: Record<
  OrgCapability,
  { label: MessageDescriptor; description: MessageDescriptor }
> = {
  ManageOrgSettings: {
    label: msg`Academy settings`,
    description: msg`Change the academy's name, country, time zone, branding and which features are on or off.`,
  },
  ManageCampuses: {
    label: msg`Campuses and rooms`,
    description: msg`Create and edit the academy's physical venues and their rooms. Only useful when the in-person modality is on.`,
  },
  ManageMembers: {
    label: msg`People and roles`,
    description: msg`Invite people, change their role in the academy and see their email address.`,
  },
  ManageAcademics: {
    label: msg`Courses and cohorts`,
    description: msg`Create programs, courses and cohorts, set their schedule and assign teachers.`,
  },
  ManageStudents: {
    label: msg`Students`,
    description: msg`Enroll, transfer and withdraw students, and keep their academic record.`,
  },
  ViewCampusData: {
    label: msg`Read academy data`,
    description: msg`See the cohorts, schedule, attendance and grades of the academy you belong to.`,
  },
};

/** All capabilities resolved for a role (`null` ⇒ no membership). */
export function capabilitiesForRole(role: OrgRole | null): CapabilityAccess[] {
  return ORG_CAPABILITIES.map((capability) => ({
    capability,
    granted: role !== null && orgRoleGrants(capability, role),
    label: CAPABILITY_COPY[capability].label,
    description: CAPABILITY_COPY[capability].description,
  }));
}

/** All nav items, flattened with their section. */
function allNavItems(): { item: NavItem; sectionId: string }[] {
  return NAV_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({ item, sectionId: section.id })),
  );
}

/** Help area for a route: `/academy/cohorts` → `academy.cohorts` ▸ `academy`. */
export function helpAreaForPath(path: string): HelpTarget | undefined {
  const segments = path.split("/").filter((s) => s.length > 0 && !s.startsWith("$"));
  // From MOST specific to most general: a sub-area with its own guide wins.
  for (let depth = Math.min(segments.length, 2); depth >= 1; depth -= 1) {
    const target = helpTargetForArea(segments.slice(0, depth).join("."));
    if (target) {
      return target;
    }
  }
  return undefined;
}

/**
 * Screen matrix for a navigation context.
 *
 * The evaluation order is what gives the correct "why": ROLE first
 * (`item.visible`), then FEATURE. A student doesn't see "Cashier" because
 * they aren't a cashier — telling them "your academy turned off the
 * cashier" would be a lie, and turning it on wouldn't open anything for them.
 */
export function screensForContext(
  ctx: NavContext,
  blockerOf: (featureKey: string) => string | undefined = () => undefined,
): ScreenAccess[] {
  return allNavItems().map(({ item, sectionId }) => {
    const base = {
      testid: item.testid,
      to: item.to,
      label: item.label,
      sectionId,
      feature: item.feature,
      help: helpAreaForPath(item.to),
    };
    if (!item.visible(ctx)) {
      return { ...base, allowed: false, reason: "role" as const };
    }
    if (item.feature !== undefined && !ctx.featureEnabled(item.feature)) {
      return {
        ...base,
        allowed: false,
        reason: "feature" as const,
        blockedBy: blockerOf(item.feature) ?? item.feature,
      };
    }
    return { ...base, allowed: true };
  });
}

/**
 * §M2 · OFFERABLE guide for a screen: only if the user can use it.
 *
 * This is the rule ("never show documentation for something the user can't
 * use") expressed ONCE, so the screen consumes it and the test pins it on
 * the same function — not on a condition copied in the JSX.
 */
export function offeredHelpFor(screen: ScreenAccess): HelpTarget | undefined {
  return screen.allowed ? screen.help : undefined;
}

/** Readable name of a feature (manifest); falls back to the key if absent. */
export function featureLabel(key: string, graph: ReadonlyMap<string, FeatureGraphNode>): string {
  return graph.get(key)?.name ?? key;
}
